import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { apiClient } from '../../../shared/services/apiClient';
import './login.css';

const schema = z.object({
    newPassword: z.string()
        .min(8, 'Minimum 8 characters')
        .regex(/[A-Z]/, 'Must contain uppercase')
        .regex(/[0-9]/, 'Must contain a number')
        .regex(/[@#$%^&*]/, 'Must contain a special character'),
});

type FormData = z.infer<typeof schema>;

export const ResetPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') ?? '';
    const [error, setError] = useState('');

    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
        resolver: zodResolver(schema),
    });

    const onSubmit = async (data: FormData) => {
        setError('');
        try {
            await apiClient.post('/auth/reset-password', { token, newPassword: data.newPassword });
            navigate('/login', { state: { message: 'Password reset successfully. Please sign in.' } });
        } catch (err: any) {
            setError(err.response?.data?.message ?? 'Reset failed. The link may have expired.');
        }
    };

    if (!token) {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-error">Invalid or missing reset token.</div>
                    <div className="login-footer"><Link to="/forgot-password">Request a new link</Link></div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="login-wrapper">
                <div className="login-card">
                    <div className="login-header">
                        <h1>Reset Password</h1>
                        <p>Enter your new password below</p>
                    </div>

                    {error && <div className="login-error">{error}</div>}

                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <div className="form-group">
                            <label>New Password</label>
                            <input type="password" {...register('newPassword')} placeholder="Min 8 chars, uppercase, number, symbol" />
                            {errors.newPassword && <span className="field-error">{errors.newPassword.message}</span>}
                        </div>
                        <button type="submit" className="login-btn" disabled={isSubmitting}>
                            {isSubmitting ? 'Resetting…' : 'Reset Password'}
                        </button>
                    </form>
                </div>
            </div>
        </>
    );
};
